import {
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    ViewEncapsulation
} from '@angular/core';
import { ControlContainer, ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Editor, toHTML, Toolbar, Validators } from 'ngx-editor';
import { LinkPlugin } from '@shared/utils/editor/plugins/link.plugin';
import { LINK_VALIDATION } from '@shared/utils/editor/link-validation.const';
import { tap } from 'rxjs';
import { nodes as basicNodes, marks } from 'ngx-editor';
import { Schema } from 'prosemirror-model';
import { ColorPresetsConfig, ToolbarConfig } from './ngx-editor.config';

@Component({
    selector: 'app-editor',
    templateUrl: './editor.component.html',
    styleUrls: ['./editor.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EditorComponent),
            multi: true
        }
    ],
    standalone: false
})
export class EditorComponent implements OnInit, OnDestroy, ControlValueAccessor {
    @Input() label?: string;
    @Input() placeholder?: string;
    @Input() required? = false;
    @Input()
    set disabled(value: boolean) {
        this._disabled = value;
        if (this.formControl) {
            if (value) {
                this.formControl.disable();
            } else {
                this.formControl.enable();
            }
        }
    }
    get disabled(): boolean {
        return this._disabled;
    }
    @Input({ required: false }) maxLength;
    @Input() formControl: FormControl = new FormControl();
    @Input() formControlName: string;
    @Input()
    set value(value: string) {
        this._value = value;
        if (value !== this.formControl.value) {
            this.formControl.patchValue(value);
        }
        this.onChange(value);
        this.onTouch(value);
    }

    get value(): string {
        return this._value;
    }
    @Input() toolbarConfig: Toolbar = ToolbarConfig;
    @Input() colorPresetsConfig: string[] = ColorPresetsConfig;

    @Output() valueChange = new EventEmitter<string>();

    public editor: Editor;
    public valueWithoutHtml = '';

    protected isEditorFocused = false;

    private _value: string;
    private _disabled = false;

    constructor(
        @Optional() private controlContainer: ControlContainer,
        private eRef: ElementRef
    ) {}

    public ngOnInit(): void {
        this.initEditor();
        this.setFormControl();
    }

    public ngOnDestroy(): void {
        this.editor.destroy();
    }

    private initEditor(): void {
        const nodes = Object.assign({}, basicNodes);
        delete nodes.image;
        const schema = new Schema({
            nodes,
            marks
        });

        this.editor = new Editor({
            schema,
            history: true,
            inputRules: true,
            keyboardShortcuts: true,
            features: {
                linkOnPaste: false // disable auto-linking on paste as we will handle it manually
            },
            plugins: [new LinkPlugin()],
            linkValidationPattern: LINK_VALIDATION // set the link validation pattern for the link component
        });
        this.valueWithoutHtml = this.convertHTMLtoText(this.value);
        this.editor.valueChanges
            .pipe(
                tap(value => {
                    let valueHTML = this.hasValue(value) ? toHTML(value) : '';
                    this.valueWithoutHtml = this.convertHTMLtoText(valueHTML);
                    if (!this.valueWithoutHtml) {
                        valueHTML = '';
                    }

                    this.onChange(valueHTML);
                    this.valueChange.emit(valueHTML);
                })
            )
            .subscribe();
    }

    private setFormControl(): void {
        this.formControl =
            (this.controlContainer?.control.get(this.formControlName) as FormControl) || this.formControl;
        if (this.maxLength) {
            this.formControl.addValidators(Validators.maxLength(this.maxLength));
        }
        if (this.disabled) {
            this.formControl.disable();
        }
    }

    private hasValue(value: Record<string, any>): boolean {
        const content = value.content as Record<string, any>[];
        return content.some(doc => doc?.content?.[0]?.type !== '');
    }

    private convertHTMLtoText(htmlStr: string): string {
        const temp = document.createElement('DIV');
        temp.innerHTML = htmlStr ?? '';
        return temp.textContent || temp.innerText || '';
    }

    @HostListener('document:click', ['$event'])
    @HostListener('document:keyup.tab', ['$event'])
    @HostListener('document:keyup.shift', ['$event'])
    protected clickout(event) {
        const richTextEditorDiv = this.eRef.nativeElement.querySelector('.rich-text-editor');
        this.isEditorFocused =
            !(richTextEditorDiv && !richTextEditorDiv.contains(event.target)) && !this.formControl.disabled;
    }

    onChange: any = () => {
        /* Form control change handler */
    };
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onTouch: any = () => {};
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onValidatorChange: any = () => {};

    writeValue(value: string): void {
        this.value = value;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouch = fn;
    }

    setDisabledState(): void {
        /* Disabled state handler */
    }

    registerOnValidatorChange(fn: () => void): void {
        this.onValidatorChange = fn;
    }
}
